import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Plan } from "../types";
import { fmtDate } from "../format";
import { useToast } from "../toast";
import PlanDialog from "../dialogs/PlanDialog";
import PlanSubscribeDialog from "../dialogs/PlanSubscribeDialog";
import PlanCodesDialog from "../dialogs/PlanCodesDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

export default function PlanDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const api = useAuthApi();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [managingCodes, setManagingCodes] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<Plan>(`${MGMT}/plans/${id}`);
      setPlan(data);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!plan) return;
    try {
      await api(`${MGMT}/plans/${plan.id}`, { method: "DELETE" });
      toast.show("Plan deleted");
      onBack();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;
  if (!plan) return <Typography color="error">Plan not found</Typography>;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>{plan.name}</Typography>
        <Chip label={plan.is_active ? "Active" : "Inactive"} color={plan.is_active ? "success" : "default"} />
        {plan.requires_code && <Chip label="Code required" icon={<VpnKeyIcon />} color="warning" variant="outlined" />}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button
          variant="outlined"
          startIcon={<PersonAddIcon />}
          onClick={() => setSubscribing(true)}
          disabled={!plan.is_active}
        >
          Subscribe Driver
        </Button>
        <Button variant="outlined" startIcon={<VpnKeyIcon />} onClick={() => setManagingCodes(true)}>
          Manage Access Codes
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setDeleting(true)}>
          Delete
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Scope</Typography>
              <Stack spacing={0.75}>
                <Typography variant="body2">
                  <strong>Charger target:</strong>{" "}
                  {plan.charger_config
                    ? (plan.charger_config.display_name ?? plan.charger_config.charge_point_id)
                    : plan.charger_group
                      ? `Group: ${plan.charger_group.name}`
                      : "All chargers"}
                </Typography>
                <Typography variant="body2">
                  <strong>Tariff:</strong> {plan.tariff ? `${plan.tariff.name} (${plan.tariff.currency})` : "Charger default"}
                </Typography>
                <Typography variant="body2">
                  <strong>Eligibility:</strong>{" "}
                  {plan.driver
                    ? plan.driver.name
                    : plan.driver_group
                      ? `Group: ${plan.driver_group.name}`
                      : "Open to all drivers"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Details</Typography>
              <Stack spacing={0.75}>
                <Typography variant="body2">
                  <strong>Created:</strong> {fmtDate(plan.created_at)}
                </Typography>
                <Typography variant="body2">
                  <strong>ID:</strong> {plan.id}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {plan.description || "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>External eligibility webhook:</strong> {plan.external_eligibility_webhook_url || "—"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PlanDialog
        open={editing}
        plan={plan}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          load();
        }}
      />

      <PlanSubscribeDialog
        open={subscribing}
        plan={plan}
        onClose={() => setSubscribing(false)}
        onSubscribed={() => setSubscribing(false)}
      />

      <PlanCodesDialog
        open={managingCodes}
        plan={plan}
        onClose={() => setManagingCodes(false)}
      />

      <ConfirmDialog
        open={deleting}
        title="Delete plan?"
        message={`Delete plan "${plan.name}"? Existing contracts created from this plan will not be affected.`}
        onCancel={() => setDeleting(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
